import { Theme as AugmentedTheme } from '@mui/material/styles';


import { makeStyles } from 'tss-react/mui';
import React, { useEffect, useState } from 'react';
import { TFunction, useTranslation } from 'react-i18next';
import { RouteComponentProps, useHistory } from 'react-router-dom';
import {
  ApiButton,
  Box,
  Button,
  ContentTemplate,
  Grid,
  HeaderTemplate,
  KeyValue,
  MUIDataTable,
  MUIDataTableColumnDef,
  SearchBar,
  SectionHeader,
  Stepper,
  TableActionTypes,
  TitleHeader,
  Typography,
} from 'web-library';
import { Radio } from '@mui/material';
import {
  MUIDataTableOptions,
  TABLE_INITIAL_STATE,
  TableState,
  useDataTable,
} from 'web-library/stories/organism/DataTable/DataTable';
import { AxiosError } from 'axios';
import { UseMutateFunction } from 'react-query';
import {
  SearchCandidateListingData,
  usePostCoordinatorAppealSearchCandidateListing,
} from '../../api-refactored/appeal/usePostCoordinatorAppealSearchCandidateListing';
import {
  ProceedWithCandidateRequest,
  ProceedWithCandidateResponse,
  usePostCoordinatorAppealProceedWithCandidate,
} from '../../api-refactored/appeal/usePostCoordinatorAppealProceedWithCandidate';

import RouteKey from '../../routes/key';
import { GenericErrorResponse } from '../../api';

const { appeal } = RouteKey.child;
const { selectSubject, applicationsCoordinator } = RouteKey.appeal.child;

const useStyles = makeStyles()((theme) =>
({
  stepper: {
    position: 'relative',
    left: '-9.5%',
    [theme.breakpoints.between('sm', 'lg')]: {
      left: '-10%',
    },
    [theme.breakpoints.between('xs', 'md')]: {
      left: '-5%',
    },
  },
  dataTableContainer: {
    marginBottom: theme.spacing(5),
  },
  searchInput: {
    marginBottom: theme.spacing(3),
  },
  container: {
    alignItems: 'center',
  },
  button: {
    height: 'fit-content',
    marginRight: theme.spacing(2),
    [theme.breakpoints.down('sm')]: {
      marginRight: theme.spacing(1),
    },
  },
  errorText: {
    color: theme.color.state.stateError,
  },
  buttonContainer: {
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(7),
  },
}));

const getTableColumns = (tableState: TableState, t: TFunction<'translation'>) => {
  const tableColumns: MUIDataTableColumnDef[] = [
    {
      name: 'candidateId',
      label: t('appeal->>AppealCoordinatorSelectCandidate.nricFinPassportNo'),
      options: {
        setCellProps: () => ({ style: { paddingTop: '33px' } }),
        customBodyRenderLite: (dataIndex: number) => (
          <Typography variant="body2">{tableState.data[dataIndex]?.candidateId}</Typography>
        ),
      },
    },
    {
      name: 'indexNo',
      label: t('appeal->>AppealCoordinatorSelectCandidate.indexNo'),
      options: {
        setCellProps: () => ({ style: { width: '30%', paddingTop: '33px' } }),
        customBodyRenderLite: (dataIndex: number) => (
          <Typography variant="body2">{tableState.data[dataIndex]?.indexNo}</Typography>
        ),
      },
    },
    {
      name: 'name',
      label: t('appeal->>AppealCoordinatorSelectCandidate.candidateName'),
      options: {
        setCellProps: () => ({ style: { width: '30%', paddingTop: '33px' } }),
        customBodyRenderLite: (dataIndex: number) => (
          <Typography variant="body2">{tableState.data[dataIndex]?.name}</Typography>
        ),
      },
    },
  ];
  return tableColumns;
};

const handleProceedClick = (
  state: TableState,
  selectedCandidateId: number[],
  setErrMsg: React.Dispatch<React.SetStateAction<string>>,
  t: TFunction<'translation'>,
  mutatePostProceedWithCandidate: UseMutateFunction<
    ProceedWithCandidateResponse,
    AxiosError<GenericErrorResponse>,
    ProceedWithCandidateRequest
  >,
  examCodeDescriptionToken: {
    examCode: string;
    examDescription: string;
    token?: string;
  }
) => {
  if (selectedCandidateId.length === 0) {
    setErrMsg(t('appeal->>AppealCoordinatorSelectCandidate.PleaseSelectACandidate'));
  } else {
    const dataIndex = selectedCandidateId[0];
    const { candidateId, indexNo } = state.data[dataIndex];
    mutatePostProceedWithCandidate({
      indexNumber: indexNo,
      nricFin: candidateId,
      examCode: examCodeDescriptionToken?.examCode,
      token: examCodeDescriptionToken?.token || null,
    });
  }
};

const onBack = (history: RouteComponentProps['history']) => {
  history.goBack();
};

const CustomCheckbox = (props: {}, { radioButton }: Record<string,string>) => {
  const newProps = { ...props };
  return <Radio className={radioButton} size="medium" {...newProps} />;
};

interface CoordinatorSelectCandidateProps {
  title: string;
  routeSteps: { label: string }[];
}

const CoordinatorSelectCandidate = ({ title, routeSteps }: CoordinatorSelectCandidateProps) => {
  const { classes } = useStyles();
  const { t } = useTranslation();
  const history = useHistory<{
    examCodeDescriptionToken: { examCode: string; examDescription: string; token?: string };
  }>();

  const [errMsg, setErrMsg] = useState<string>('');

  const [selectedCandidateId, setSelectedCandidateId] = useState<number[]>([]);

  const {
    mutate: mutatePostSearchCandidateListing,
    isLoading: isLoadingSearchCandidateListing,
  } = usePostCoordinatorAppealSearchCandidateListing({
    onSuccess: (data: SearchCandidateListingData) => {
      tableDispatch({
        type: 'UPDATE_DATA',
        payload: {
          data: data.content,
          count: data.totalElements,
        },
      } as TableActionTypes);
    },
  });

  const { state, dispatch: tableDispatch, tOptions } = useDataTable({
    ...TABLE_INITIAL_STATE,
    sortColumn: 'name',
    order: 'asc',
    size: 5,
    page: 0,
    search: '',
  });

  const examCodeDescriptionToken = history?.location?.state?.examCodeDescriptionToken;

  useEffect(() => {
    mutatePostSearchCandidateListing({
      apiPathParam: {
        page: state.page,
        pageSize: state.size,
      },
      q: state.search || '',
      examCode: examCodeDescriptionToken?.examCode,
      token: examCodeDescriptionToken?.token || null,
    });
  }, [
    examCodeDescriptionToken?.examCode,
    mutatePostSearchCandidateListing,
    state.page,
    state.search,
    state.size,
    examCodeDescriptionToken?.token,
  ]);

  const customOptions: MUIDataTableOptions = {
    ...tOptions,
    selectableRows: 'single',
    selectToolbarPlacement: 'none',
    onRowSelectionChange: (currentRowsSelected: any, allRowsSelected: any, rowsSelected: any) => {
      if (tOptions.onRowSelectionChange) {
        // perform dataTable web-library onChange
        tOptions.onRowSelectionChange(currentRowsSelected, allRowsSelected, rowsSelected);
      }
      setSelectedCandidateId(rowsSelected || []);
    },
    sort: false,
    pagination: true,
  };

  useEffect(() => {
    tableDispatch({
      type: 'CLEAR_SELECTION',
    } as TableActionTypes);
  }, [tableDispatch, state.order, state.search]);

  const {
    mutate: mutatePostProceedWithCandidate,
    status: statusProceedWithCandidate,
  } = usePostCoordinatorAppealProceedWithCandidate({
    onSuccess: (data: ProceedWithCandidateResponse) => {
      history.push(
        `/${appeal}/${applicationsCoordinator.route}/${data.uuid}/${selectSubject.route}`
      );
    },
    onError: (err: AxiosError<GenericErrorResponse>) => {
      const data = err?.response?.data?.data;
      const code = err?.response?.data?.code;
      if (code === '1006') {
        setErrMsg(
          t(
            'appeal->>AppealCoordinatorSelectCandidate.CandidateAlreadySelectedByOtherCoordinator',
            {
              coordinator: data,
            }
          )
        );
      }
    },
  });

  return (
    <>
      <TitleHeader title={title} />
      <HeaderTemplate>
        <KeyValue
          keyText={t('appeal->>AppealCoordinatorSelectSubject.ExamLevel')}
          valueText={examCodeDescriptionToken?.examDescription}
        />
        <Box className={classes.stepper}>
          <Stepper activeStep={0} steps={routeSteps} />
        </Box>
      </HeaderTemplate>
      <Box>
        <ContentTemplate isContinuable type="full">
          <SectionHeader title={t('appeal->>AppealCoordinatorSelectCandidate.SelectCandidate')} />
          <Box className={classes.dataTableContainer}>
            <SearchBar
              className={classes.searchInput}
              placeholder={t(
                'appeal->>AppealCoordinatorSelectCandidate.SearchCandidatePlaceholder'
              )}
              name="searchInput"
              ariaLabel="searchInput"
              defaultValue={state.search}
              onSearch={(searchStr) => {
                tableDispatch({
                  type: 'UPDATE_SEARCH',
                  payload: searchStr.trim(),
                });
              }}
            />
            <MUIDataTable
              state={state}
              dispatch={tableDispatch}
              columns={getTableColumns(state, t)}
              isLoading={isLoadingSearchCandidateListing}
              data={state.data}
              options={customOptions}
              rowsPerPageOptions={[5, 10, 15, 20, 25]}
              components={{
                Checkbox: CustomCheckbox,
              }}
            />
          </Box>
        </ContentTemplate>
        <ContentTemplate continuous>
          {errMsg && (
            <Grid xs={12} md={8}>
              <Typography className={classes.errorText} display="inline" variant="body2">
                {errMsg}
              </Typography>
            </Grid>
          )}
          <Grid
            container
            direction="row"
            alignItems="flex-start"
            spacing={3}
            className={classes.buttonContainer}
          >
            <Grid xs={12} md={3}>
              <Button
                id="button-selectSubject-back"
                fullWidth
                className={classes.button}
                variant="outlined"
                onClick={() => {
                  onBack(history);
                }}
              >
                {t('appeal->>AppealCoordinatorSelectCandidate.Back')}
              </Button>
            </Grid>

            <Grid xs={12} md={5}>
              <ApiButton
                id="button-selectSubject-continueToNextPage"
                status={statusProceedWithCandidate}
                fullWidth
                className={classes.button}
                onClick={() =>
                  handleProceedClick(
                    state,
                    selectedCandidateId,
                    setErrMsg,
                    t,
                    mutatePostProceedWithCandidate,
                    examCodeDescriptionToken
                  )
                }
              >
                {t('appeal->>AppealCoordinatorSelectCandidate.ProceedWithCandidate')}
              </ApiButton>
            </Grid>
          </Grid>
        </ContentTemplate>
      </Box>
    </>
  );
};

export default CoordinatorSelectCandidate;
